<template>
  <AppSection :class="['wrapper', { home }]" unpadded>
    <MobileOnly class="flex justify-center" :class="{ 'img-container pt-3': !home }">
      <HeroVideo v-if="home" />
      <HeroImg v-if="img" :img />
    </MobileOnly>

    <DesktopOnly css>
      <AppContainer>
        <div class="flex justify-between">
          <div class="content">
            <h1 class="mb-4">
              <span v-html="header"  class="text-start" />
              <span class="mb-2">
                <CountryFlag v-if="extraIso" :code="extraIso" :size="55" end />
                <CountryFlag v-if="product" :code="getVisaCountryIsoCode(product.country)" :size="55" end />
              </span>
            </h1>
            <HeroKeyPoints v-if="product" :product />
            <p
              v-html="$tt(`page.hero_body_${product?.country || 'home'}`, { price })"
              class="mb-7 lead"
            />
            <HeroCta @click="navigate" />
          </div>
          <div class="self-center" :class="{ 'img-container flex justify-end': !home }">
            <HeroVideo v-if="home" />
            <HeroImg v-if="img" :img />
          </div>
        </div>
      </AppContainer>
    </DesktopOnly>

    <MobileOnly class="content">
      <AppContainer :class="{ 'pt-4': home }">
        <h1 class="mb-4">
          <span v-html="header" class="text-center" />
          <CountryFlag v-if="product" :code="getVisaCountryIsoCode(product.country)" :size="42" class="mb-1" end />
        </h1>
        <HeroKeyPoints v-if="product" :product />
        <p
          v-html="$tt(`page.hero_body_${product?.country || 'home'}`, { price })"
          class="mb-6 lead"
        />
        <HeroCta @click="navigate" block />
      </AppContainer>
    </MobileOnly>
  </AppSection>
</template>

<script setup lang="ts">
import type { Image, Product } from '~/types';
import { getVisaCountryExtraIso, getVisaCountryIsoCode } from '~/helpers/visa-country';
import { getHashId } from '~/helpers/routing';

const props = defineProps<{
  img?: Image;
  product?: Product;
}>();

const router = useRouter();
const { ctaClickBus } = useEmitter();
const { tt } = useLocalization();
const { displayPrice } = useFormat();

const header = tt(`page.hero_header_${props.product?.country || 'home'}`);
const home = !props.product;

const extraIso = props.product && getVisaCountryExtraIso(props.product.country);
const price = props.product && displayPrice(props.product.price);

function navigate() {
  if (props.product) {
    ctaClickBus.emit();
  } else {
    router.push(`/${getHashId('countries_overview')}`);
  }
}
</script>

<style scoped lang="scss">
.wrapper {
  position: relative;
  padding-top: calc(var(--space-8) + var(--space-5));
  padding-bottom: calc(var(--space-10));
}
.img-container {
  img {
    width: 100%;
    max-width: 500px;
    margin-top: calc(-1 * var(--space-8));
    align-self: flex-start;
  }
}
.content {
  position: relative;
  z-index: 1;
  $width: calc(100% - (6 / 12) * 100%);
  flex: 0 0 $width;
  max-width: $width;
}
h1 {
  line-height: var(--lh-md);
}

@media (max-width: 850px) {
  .wrapper {
    padding-top: 0;
    padding-bottom: var(--space-8);
  }
  h1 {
    font-size: 36px;
  }
  .lead {
    font-size: 16px;
  }
  .img-container {
    display: flex !important;
    img {
      width: 65%;
      margin-top: 0;
    }
  }
  .content {
    text-align: center;
  }
  .home {
    margin-bottom: var(--space-7);
  }
}
</style>